import React, { useCallback, useEffect, useState, useRef } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Paperclip, Trash, DownloadSimple, Image as ImgIcon, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";

const ACCEPT = "image/png,image/jpeg,image/webp,image/gif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,.dwg,.dxf,application/octet-stream";

export function Attachments({ parentType, parentId, canEdit = true, category }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    if (!parentId) return;
    const params = { parent_type: parentType, parent_id: parentId };
    if (category) params.category = category;
    const { data } = await api.get(`/attachments`, { params });
    setFiles(data);
  }, [parentType, parentId, category]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File too large (max 20 MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const params = { parent_type: parentType, parent_id: parentId };
      if (category) params.category = category;
      await api.post(`/attachments/upload`, fd, {
        params,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded");
      if (inputRef.current) inputRef.current.value = "";
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this attachment?")) return;
    try {
      await api.delete(`/attachments/${id}`);
      toast.success("Deleted");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const download = async (f) => {
    try {
      const { triggerFileDownload } = await import("@/lib/apiClient");
      await triggerFileDownload(`/attachments/${f.id}/download`, f.original_filename || "attachment");
    } catch { toast.error("Download failed"); }
  };

  const humanSize = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <div className="space-y-2" data-testid={`attachments-${parentType}-${parentId}`}>
      <div className="flex items-center justify-between">
        <div className="label-caps flex items-center gap-1"><Paperclip size={12} /> Attachments</div>
        {canEdit && (
          <>
            <input
              ref={inputRef} type="file" accept={ACCEPT}
              onChange={upload} className="hidden"
              data-testid={`attachment-file-input-${parentType}-${parentId}`}
            />
            <button
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid={`attachment-upload-btn-${parentType}-${parentId}`}
              className="text-xs uppercase tracking-widest font-semibold text-[#EA580C] hover:text-[#c2410c] disabled:opacity-50"
            >
              {uploading ? "Uploading…" : "+ Upload"}
            </button>
          </>
        )}
      </div>

      {files.length === 0 ? (
        <div className="text-xs text-slate-400 py-2">No files attached.</div>
      ) : (
        <div className="space-y-1">
          {files.map((f) => (
            <div key={f.id} className="flex items-center gap-2 bg-slate-50 border border-slate-200 px-2 py-1.5 rounded-sm">
              {f.content_type === "application/pdf" ? (
                <FilePdf size={16} className="text-red-600 shrink-0" />
              ) : (
                <ImgIcon size={16} className="text-sky-600 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{f.original_filename}</div>
                <div className="text-[11px] text-slate-500">{humanSize(f.size)}</div>
              </div>
              <button onClick={() => download(f)} className="text-slate-500 hover:text-[#EA580C] p-1" title="Download">
                <DownloadSimple size={14} />
              </button>
              {canEdit && (
                <button onClick={() => del(f.id)} className="text-slate-400 hover:text-red-600 p-1" title="Delete">
                  <Trash size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
